import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tag, Plus, Trash2, Inbox } from "lucide-react";

export default function CategoriesPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await api.get("/categories");
      setCategories(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setCreating(true);
    try {
      await api.post("/categories", { name: trimmed });
      setNewName("");
      fetchCategories();
      toast.success(`Category "${trimmed}" created`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (deleteConfirm !== id) {
      setDeleteConfirm(id);
      setTimeout(() => setDeleteConfirm(null), 3000);
      return;
    }
    try {
      await api.delete(`/categories/${id}`);
      fetchCategories();
      toast.success("Category deleted");
    } catch {
      toast.error("Failed to delete");
    }
    setDeleteConfirm(null);
  };

  return (
    <div className="h-full flex flex-col page-enter" data-testid="categories-page">
      <div className="px-4 sm:px-6 pt-4 pb-3 border-b border-[#E5E5E5] flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-medium text-[#111111]" data-testid="categories-title">Categories</h2>
          <p className="text-sm text-zinc-500 mt-0.5">Workflow: what do you need to do with this email?</p>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-4 sm:px-6 py-5 space-y-4">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Category list */}
            <div className="space-y-1">
              {categories.map((cat) => (
                <div
                  key={cat.category_id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F5F5F5] transition-colors group cursor-pointer"
                  onClick={() => navigate(`/inbox?category=${cat.category_id}`)}
                  data-testid={`category-item-${cat.category_id}`}
                >
                  <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                  <span className="text-sm text-[#555555] flex-1">{cat.name}</span>
                  <Inbox size={14} className="text-zinc-300 group-hover:text-zinc-500 shrink-0" />
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(cat.category_id); }}
                    className={`p-1 rounded shrink-0 transition-colors ${
                      deleteConfirm === cat.category_id
                        ? "text-red-500 bg-red-50"
                        : "text-zinc-300 hover:text-red-600 opacity-0 group-hover:opacity-100"
                    }`}
                    data-testid={`category-delete-${cat.category_id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>

            {/* Create new */}
            {categories.length < 10 && (
              <div className="flex items-center gap-2 pt-2">
                <Plus size={14} className="text-zinc-400 shrink-0" />
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                  placeholder="New category..."
                  className="h-9 text-sm"
                  data-testid="category-new-input"
                />
                {newName.trim() && (
                  <Button size="sm" onClick={handleCreate} disabled={creating} className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-3" data-testid="category-create-btn">
                    Add
                  </Button>
                )}
              </div>
            )}

            <p className="text-[10px] text-zinc-400 pt-2" data-testid="category-counter">
              {categories.length} / 10 categories used
            </p>
          </>
        )}
      </div>
    </div>
  );
}
